<script setup lang="ts">
    import { computed, reactive, ref, watch, onMounted } from 'vue';
    import { RouterView, useRoute } from 'vue-router';
    import List from '@shared/components/List/List.vue';
    import ContactNameEmail from '@shared/components/ContactNameEmail.vue';
    import { formatDateTime } from '@shared/utils/formatDateTime';
    import { buildQueryString, getResponses, getContactResponses, deleteResponses, type DateRange } from '../SurveyStatistics.access';
    import type { OrderByDirection } from '@shared/components/List/ListTypes';
    import { bsAlert, AlertTypes } from '@shared/components/BsAlerts';
    import type { QuestionFilter, SurveyResponseListItem } from '../../SurveysTypes';

    const props = defineProps<{
        surveyId: string;
        dateRange: DateRange;
        questionFilters: QuestionFilter[];
        contactId?: string;
        isEventSurvey?: boolean;
    }>();

    const PAGE_SIZE = 25;

    const route = useRoute();

    const showingResponse = computed(() => route.name === 'SurveyStatisticsResponse');
    const showingContact = computed(() => route.name === 'SurveyStatisticsResponseContact');
    const contactId = computed(() => props.contactId ?? null);
    const isCheckable = computed(() => !contactId.value && !props.isEventSurvey);
    const questionOptionId = computed(() => (typeof route.query.questionOptionId === 'string' ? route.query.questionOptionId : null));

    const orderOptions = [
        { key: 'Created', display: $Resources.GenericCreatedLabel },
        { key: 'Contact/FirstName,Contact/LastName', display: $Resources.ContactsSortByNameLabel },
        { key: 'Contact/Email', display: $Resources.ContactsSortByEmailLabel },
    ];

    const state = reactive<{
        items: SurveyResponseListItem[];
        loading: boolean;
        totalCount: number;
    }>({
        items: [],
        loading: false,
        totalCount: 0,
    });

    const filter = ref('');
    const orderBy = ref(orderOptions[0].key);
    const orderDirection = ref<OrderByDirection>('desc');
    const checkedItems = ref<string[]>([]);
    const deleting = ref(false);

    const allLoaded = computed(() => state.items.length >= state.totalCount);

    const title = computed(() => $Resources.StatisticsSurveyResponsesHeading);
    const description = computed(() => $Resources.StatisticsSurveyResponsesIntroduction);

    function buildQs(): string {
        const filters =
            contactId.value && questionOptionId.value
                ? [...props.questionFilters, { QuestionId: '', QuestionOptionId: questionOptionId.value }]
                : props.questionFilters;
        return buildQueryString(props.dateRange, filters);
    }

    function buildOrderByClause(): string {
        const dir = orderDirection.value;
        return orderBy.value
            .split(',')
            .map((key) => `${key} ${dir}`)
            .join(',');
    }

    function escapeOdata(text: string): string {
        // % must be escaped first so it doesn't double-encode the %XX sequences introduced below.
        return text
            .replace(/%/g, '%25')
            .replace(/'/g, "''")
            .replace(/#/g, '%23')
            .replace(/\+/g, '%2B')
            .replace(/&/g, '%26');
    }

    function buildExtra(): string {
        if (contactId.value) return '';

        const parts: string[] = [];
        const rawTerm = filter.value.trim().toLowerCase();
        if (rawTerm) {
            const term = escapeOdata(rawTerm);
            const smsTerm = escapeOdata(rawTerm.replace(/\+/g, ''));
            const odataFilter =
                `(indexof(tolower(Contact/FirstName), '${term}') ge 0` +
                ` or indexof(tolower(Contact/LastName), '${term}') ge 0` +
                ` or indexof(concat(concat(tolower(Contact/FirstName),' '), tolower(Contact/LastName)), '${term}') ge 0` +
                ` or indexof(tolower(Contact/Email), '${term}') ge 0` +
                ` or indexof(Contact/SmsNumber, '${smsTerm}') ge 0)`;
            parts.push(`$filter=${odataFilter.replace(/\s/g, '+')}`);
        }

        const order = buildOrderByClause();
        if (order) parts.push(`$orderby=${order}`);

        return parts.length ? '&' + parts.join('&') : '';
    }

    async function loadResponses(append = false) {
        state.loading = true;
        try {
            const skip = append ? state.items.length : 0;
            const qs = buildQs();
            const extra = buildExtra();
            const result = contactId.value
                ? await getContactResponses(props.surveyId, contactId.value, qs, skip, PAGE_SIZE)
                : await getResponses(props.surveyId, qs, skip, PAGE_SIZE, extra);
            state.items = append ? [...state.items, ...result.Results] : result.Results;
            state.totalCount = result.Count;
        } catch {
            bsAlert({ type: AlertTypes.Danger });
        } finally {
            state.loading = false;
        }
    }

    function responseRoute(surveyResponseId: string) {
        return {
            name: 'SurveyStatisticsResponse',
            params: { surveyId: props.surveyId, responseId: surveyResponseId },
            query: contactId.value ? { contactId: contactId.value } : {},
        };
    }

    function contactRoute(respondentContactId: string) {
        return { name: 'SurveyStatisticsResponseContact', params: { surveyId: props.surveyId, contactId: respondentContactId } };
    }

    function onFilterUpdate(newFilter: string) {
        filter.value = newFilter;
        loadResponses();
    }

    function onOrderUpdate(key: string, direction: OrderByDirection) {
        orderBy.value = key;
        orderDirection.value = direction;
        loadResponses();
    }

    function onCheckedItemsUpdate(newVal: (string | number)[]) {
        checkedItems.value = newVal as string[];
    }

    function onClearSelected() {
        checkedItems.value = [];
    }

    async function onDeleteSelected() {
        if (checkedItems.value.length === 0) return;
        deleting.value = true;
        try {
            await deleteResponses(props.surveyId, checkedItems.value);
            bsAlert({ type: AlertTypes.Success });
            checkedItems.value = [];
            loadResponses();
        } catch {
            bsAlert({ type: AlertTypes.Danger });
        } finally {
            deleting.value = false;
        }
    }

    watch(
        () => [props.contactId, questionOptionId.value],
        () => {
            if (showingResponse.value || showingContact.value) return;
            loadResponses();
        }
    );
    watch(
        () => [props.dateRange.start, props.dateRange.end, props.questionFilters],
        () => {
            if (showingResponse.value || showingContact.value) return;
            loadResponses();
        },
        { deep: true }
    );

    onMounted(() => loadResponses());
</script>

<template>
    <RouterView v-if="showingResponse || showingContact" />

    <div v-show="!showingResponse && !showingContact">
        <List
            :items="state.items"
            :loading="state.loading"
            :allLoaded="allLoaded"
            :totalItemCount="state.totalCount"
            :title="title"
            :description="description"
            :serverSideFilter="true"
            :hideToolbar="!!contactId"
            :orderOptions="contactId ? undefined : orderOptions"
            :orderByDirection="orderDirection"
            :orderByValue="orderBy"
            :filterRateLimit="350"
            :checkedItems="isCheckable ? checkedItems : null"
            :deleteConfirmation="true"
            :deleting="deleting"
            itemId="SurveyResponseId"
            :perPage="PAGE_SIZE"
            @update:filter="onFilterUpdate"
            @update:order="onOrderUpdate"
            @update:checkedItems="onCheckedItemsUpdate"
            @clearSelected="onClearSelected"
            @deleteSelected="onDeleteSelected"
            @loadMore="loadResponses(true)">
            <template #item="{ item: response }">
                <div class="flex-1" style="min-width: min(400px, 100%)">
                    <ContactNameEmail
                        :contactId="response.Contact.ContactId"
                        :firstName="response.Contact.FirstName"
                        :lastName="response.Contact.LastName"
                        :email="response.Contact.Email"
                        :isActive="response.Contact.IsActive"
                        :hasBounced="response.Contact.HasBounced"
                        :to="contactRoute(response.Contact.ContactId)" />

                    <p class="meta">
                        <span class="glyphicon glyphicon-time"></span>
                        {{ $Resources.SubmittedDateTimeLabel }}:
                        <time :datetime="response.Created">
                            {{ formatDateTime(response.Created) }}
                        </time>
                    </p>
                </div>

                <div class="align-self-center" style="min-width: min(240px, 100%)">
                    <router-link class="btn btn-default" :to="responseRoute(response.SurveyResponseId)">
                        {{ $Resources.SurveyShowResponse }}
                    </router-link>
                </div>
            </template>
        </List>
    </div>
</template>

